import { useCallback, useEffect, useRef, useState } from "react";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { useRouter } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useRegisterSW } from "virtual:pwa-register/react";

import { supabase } from "~/lib/supabase";
import { useUpdateStore } from "~/stores/use-update-store";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";

export default function UpdateScreen() {
  const { t } = useTranslation("common");
  const [serviceWorker, setServiceWorker] =
    useState<ServiceWorkerRegistration>();
  const [shouldUpdate, setShouldUpdate] = useState(false);
  const { setIsUpdating, isUpdating } = useUpdateStore();
  const [showUpdateDialog, setShowUpdateDialog] = useState(false);
  const forceUpdate = useRef(false);
  const router = useRouter();

  const clearCache = async () => {
    try {
      const keys = await caches.keys();
      await Promise.all(keys.map((key) => caches.delete(key)));
    } catch (err) {
      console.error("Error clearing cache:", err);
    }
  };

  const { updateServiceWorker } = useRegisterSW({
    immediate: true,
    onRegisterError(error) {
      console.error("SW registration error", error);
    },
    onNeedRefresh() {
      console.log("New version available");
      setShouldUpdate(true);

      if (forceUpdate.current) {
        console.log("FORCED UPDATE");
        void performUpdate();
      } else {
        setShowUpdateDialog(true);
      }
    },
    onRegistered(registration) {
      console.log("SW Registered:", registration);
      setServiceWorker(registration);

      if (registration) {
        // Check for updates more frequently
        setInterval(
          () => {
            if (!registration.installing) {
              registration.update().catch((err) => {
                console.error("Error checking for updates:", err);
              });
            }
          },
          30 * 60 * 1000, // Check every 30 minutes
        );
      }
    },
    onOfflineReady() {
      console.log("App is ready for offline use.");
    },
  });

  const performUpdate = useCallback(async () => {
    try {
      setIsUpdating(true);
      setShouldUpdate(false);
      setShowUpdateDialog(false);

      // Clear cache first
      await clearCache();

      if (serviceWorker?.waiting) {
        // Add a promise to ensure the service worker is activated
        const activationPromise = new Promise((resolve) => {
          if (serviceWorker.waiting) {
            serviceWorker.waiting.addEventListener("statechange", (e) => {
              if ((e.target as ServiceWorker).state === "activated") {
                resolve(true);
              }
            });
          }
        });

        serviceWorker.waiting.postMessage({ type: "SKIP_WAITING" });

        // Wait for activation
        await activationPromise;
      }

      await updateServiceWorker(true);
      setTimeout(() => {
        window.location.reload();
      }, 100);
    } catch (err) {
      console.error("Update failed:", err);
      setIsUpdating(false);
    }
  }, [serviceWorker, updateServiceWorker, setIsUpdating]);

  // Check for updates on route change
  useEffect(() => {
    const unsub = router.subscribe("onResolved", () => {
      if (!serviceWorker) return;
      // forceUpdate.current = true;
      if (shouldUpdate) {
        if (forceUpdate.current) {
          console.log("FORCED UPDATE");
          void performUpdate();
        } else {
          setShowUpdateDialog(true);
        }
      }
      console.log("CHECKING FOR UPDATES");
      serviceWorker.update().catch((err) => {
        console.error("Error checking for updates:", err);
      });
    });

    return () => unsub();
  }, [router, serviceWorker, shouldUpdate, performUpdate]);

  // Reset updating state after timeout
  useEffect(() => {
    let timeoutId: NodeJS.Timeout | undefined;

    if (isUpdating) {
      // Reduce the timeout to 2.5 seconds
      timeoutId = setTimeout(() => {
        setIsUpdating(false);
        // Force reload if update takes too long
        console.log("LOADING TOO LONG.. FORCE RELOAD");
        window.location.reload();
      }, 2500);
    }

    return () => timeoutId && clearTimeout(timeoutId);
  }, [isUpdating, setIsUpdating]);

  // Listen for update broadcasts from Supabase
  useEffect(() => {
    const channel = supabase
      .channel("update-channel")
      .on<{ force: boolean }>(
        "broadcast",
        { event: "update-available" },
        (payload) => {
          setShouldUpdate(true);
          if (payload.payload.force) {
            console.log("Forced update received");
            forceUpdate.current = true;
            void performUpdate();
          } else {
            setShowUpdateDialog(true);
          }
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel).catch((e) => console.error(e));
    };
  }, [performUpdate]);

  return (
    <>
      <Dialog open={showUpdateDialog} onOpenChange={setShowUpdateDialog}>
        <DialogContent overlayClassName="z-[9999999999]"
          className="max-w-lg w-full z-[9999999999]"
          onInteractOutside={(e) => {
            e.preventDefault();
          }}
        >
          <DialogHeader>
            <DialogTitle>{t("update_available")}</DialogTitle>
            <VisuallyHidden>
              <DialogDescription></DialogDescription>
            </VisuallyHidden>
          </DialogHeader>
          <div className="max-h-[80vh] p-4 overflow-auto small-scrollbar">
            <p className="text-center">{t("update_available_description")}</p>
          </div>
          <DialogFooter>
            <div className="flex flex-col gap-2 w-full">
              <Button
                onClick={() => void performUpdate()}
                className="w-full"
                variant="success"
              >
                {t("update_now")}
              </Button>
              <Button
                onClick={() => setShowUpdateDialog(false)}
                className="w-full"
                variant="destructive"
              >
                {t("continue_working")}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {isUpdating && (
        <div className="absolute inset-0 z-[999999] bg-black/80 flex items-center justify-center">
          <p className="text-4xl font-bold">{t("updating")}</p>
        </div>
      )}
    </>
  );
}
